import { useScreenWidth } from "@msys/ui";
import { Button } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { RestrictedByProjectPermissionWithDebug } from "../../auth/RestrictedByProjectPermission.js";
import { CancelInvoiceButton } from "./buttons/CancelInvoiceButton.js";
import { OpenInvoiceButton } from "./buttons/OpenInvoiceButton.js";
import { PayInvoiceButton } from "./buttons/PayInvoiceButton.js";
import {
  InvoiceAction_InvoiceFragment,
  InvoiceAction_ProjectFragment,
} from "./InvoiceAction.generated.js";

interface Props {
  invoice: InvoiceAction_InvoiceFragment;
  project: InvoiceAction_ProjectFragment;
  onOpenPreview?: () => void;
}

export const InvoiceAction = ({ invoice, project, onOpenPreview }: Props) => {
  const { isMinDesktop } = useScreenWidth();
  const { t } = useTranslate(["InvoiceDetailsForm", "Invoices"]);

  // Percentage-based invoices (down_payment / advance_payment) carry no
  // invoiceItems/invoiceExtraItems — their billable line lives in
  // invoicePaymentItems. Count payment items so those invoices aren't
  // treated as empty, while a genuinely empty invoice stays blocked.
  const hasNoLineItems =
    (invoice.invoiceItems?.length ?? 0) +
      (invoice.invoiceExtraItems?.length ?? 0) +
      (invoice.invoicePaymentItems?.length ?? 0) <
    1;

  const actionButtons: React.ReactNode[] = [
    invoice.status === "draft" && !isMinDesktop && onOpenPreview && (
      <Button
        color="primary"
        variant="outlined"
        onClick={onOpenPreview}
        key={"show preview"}
      >
        {t("Preview", {
          ns: "InvoiceDetailsForm",
        })}
      </Button>
    ),
    invoice.status === "draft" && (
      <RestrictedByProjectPermissionWithDebug
        permission="MANAGE_INVOICES"
        project={project}
        key={"send invoice to client"}
      >
        {invoice.status === "draft" && (
          <OpenInvoiceButton
            invoiceId={invoice.id}
            projectId={project.id}
            docId={invoice.quote.id}
            disabled={
              !invoice.invoiceTexts?.opening ||
              !invoice.invoiceTexts?.closing ||
              hasNoLineItems
            }
            disabledTooltip={
              hasNoLineItems
                ? t("Add at least one line item before sending the invoice.", {
                    ns: "Invoices",
                  })
                : undefined
            }
            paymentTermDuration={invoice.paymentTermDuration}
            deliveryOfServiceStart={invoice.deliveryOfServiceStart}
            deliveryOfServiceEnd={invoice.deliveryOfServiceEnd}
          />
        )}
      </RestrictedByProjectPermissionWithDebug>
    ),
    invoice.status !== "draft" && (
      <RestrictedByProjectPermissionWithDebug
        permission="MANAGE_INVOICES"
        project={project}
        key={"cancel invoice"}
      >
        {invoice.status === "open" && (
          <CancelInvoiceButton
            invoiceId={invoice.id}
            projectId={project.id}
            docId={invoice.quote.id}
          />
        )}
      </RestrictedByProjectPermissionWithDebug>
    ),
    invoice.status !== "draft" && (
      <RestrictedByProjectPermissionWithDebug
        permission="MANAGE_INVOICES"
        project={project}
        key={"mark invoice as paid"}
      >
        {invoice.status === "open" && (
          <PayInvoiceButton
            invoiceId={invoice.id}
            projectId={project.id}
            docId={invoice.quote.id}
          />
        )}
      </RestrictedByProjectPermissionWithDebug>
    ),
  ].filter(Boolean);

  return actionButtons;
};
